import { Component, effect, signal, inject, OnInit, computed } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Data2Service } from '@/common/data2.service';
import { DateService } from '@/common/date.service';
import { TranslationOrchestratorService } from '@/common/translation/translation-orchestrator.service';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { TranslationLanguagesEnum } from '@/common/translation/translation-languages-enum';
import { TranslatePipe } from '@ngx-translate/core';
import { FormatDateTimePipe } from '@/common/pipes/format-date-time.pipe';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslationLanguageSelectComponent } from '@/common/translation/translation-language-select.component';
import { ERPCurrency, EventBusinessIssue } from '@/event-business/event-business.model';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';

@Component({
	selector: 'app-event-business-documents',
	templateUrl: './event-business-documents.component.html',
	standalone: true,
	styleUrls: ['./event-business-documents.component.css'],
	imports: [
		TranslatePipe,
		NavbarModule,
		ReactiveFormsModule,
		TranslationLanguageSelectComponent,
		MatButtonModule,
		MatFormField,
		MatOption,
		MatSelect,
		MatLabel,
		FormsModule,
		FormatDateTimePipe,
	],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				subscriptSizing: 'dynamic',
			},
		},
	],
})
export class EventBusinessDocumentsComponent implements OnInit {
	languagesSupported: TranslationLanguagesEnum[] = [
		TranslationLanguagesEnum.POLISH,
		TranslationLanguagesEnum.ENGLISH,
	];

	protected issued: Array<EventBusinessIssue> = [];
	protected expiredDocumentExists = false;
	protected readonly currency = signal(ERPCurrency.PLN);
	protected title = computed(() => `Windykacja ${this.currency().toUpperCase()}`);

	private readonly eventBusinessIssueDaysExpired = new Map<string, number>();
	private readonly eventID: string = null;

	private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
	private readonly translationOrchestratorService: TranslationOrchestratorService =
		inject(TranslationOrchestratorService);
	private readonly api: Data2Service = inject(Data2Service);
	private readonly date: DateService = inject(DateService);

	constructor() {
		this.eventID = this.activatedRoute.snapshot.params['id'];

		effect(() => {
			if (this.currency) {
				this.fetchData();
			}
		});
	}

	ngOnInit(): void {
		this.fetchData();
	}

	print(): void {
		window.print();
	}

	protected getGetExpiredDaysCountForIssue(issue: EventBusinessIssue): number {
		return this.eventBusinessIssueDaysExpired.get(issue.id) ?? -1;
	}

	protected changeLanguage(lang: TranslationLanguagesEnum): void {
		this.translationOrchestratorService.load('erp/event-business/event-business-issues', lang);
		this.translationOrchestratorService.load('currency', lang);
	}

	private fetchData(): void {
		this.api.get(
			`/eventBusinessIssues?eventBusiness=${this.eventID}&currency=${this.currency()}&expiredOnly=true&settledOnly=true`,
			(result) => {
				this.expiredDocumentExists = result.data.length > 0;

				result.data.forEach((issue: EventBusinessIssue) =>
					this.eventBusinessIssueDaysExpired.set(
						issue.id,
						this.date.daysBetween(issue.due, this.date.now().date),
					),
				);

				this.issued = result.data;
			},
		);
	}
}
